import {
  AdminTranslationList,
  AdminTranslationQuery,
  Language,
  Route,
  TranslationDictionaryResponse,
  UpdateTranslation,
  populateRoute,
} from "@repo/common"
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"
import { publicRequest } from "../public-api"

async function fetchTranslationsAdmin(
  query: AdminTranslationQuery,
): Promise<AdminTranslationList> {
  return await request<AdminTranslationList>({
    url: Route.TRANSLATION__ADMIN_FIND_ALL,
    method: "GET",
    params: query,
  })
}

async function fetchDictionary(
  language: Language,
): Promise<TranslationDictionaryResponse> {
  return await publicRequest<TranslationDictionaryResponse>({
    url: populateRoute(Route.TRANSLATION__FIND_PUBLIC, language),
    method: "GET",
  })
}

async function updateTranslationAdmin(
  key: string,
  dto: UpdateTranslation,
): Promise<void> {
  return await request<void>({
    url: populateRoute(
      Route.TRANSLATION__ADMIN_UPDATE,
      encodeURIComponent(key),
    ),
    method: "PUT",
    data: dto,
  })
}

export function useUpdateTranslationAdmin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ key, dto }: { key: string; dto: UpdateTranslation }) =>
      updateTranslationAdmin(key, dto),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: translationQueries.all() }),
  })
}

const translationQueries = {
  all: () => ["translations"],
  adminList: (query: AdminTranslationQuery = {}) =>
    queryOptions({
      queryKey: [...translationQueries.all(), "admin-list", query],
      queryFn: () => fetchTranslationsAdmin(query),
      placeholderData: keepPreviousData,
    }),
  dictionary: (language: Language) =>
    queryOptions({
      queryKey: [...translationQueries.all(), "dictionary", language],
      queryFn: () => fetchDictionary(language),
      // New texts show at once during development.
      staleTime: import.meta.env.DEV ? 0 : 5 * 60 * 1000,
    }),
}

export default translationQueries
